<script setup lang="ts">
import { useData } from "vitepress";
import { computed } from "vue";
import { data as articles } from "../../../../articles/index.data.js";

const { page } = useData();

// Prev/next is a reading sequence, so it stays within one collection: a
// project's articles chain to each other, and standalone articles chain to
// each other. They are still merged everywhere else (index, homepage, tags).
const projectPrefix = computed(() => {
  const match = page.value.relativePath.match(/^projects\/([^/]+)\//);
  return match ? `/projects/${match[1]}/` : null;
});

const scopedArticles = computed(() => {
  const prefix = projectPrefix.value;
  if (prefix) {
    return articles.filter((article) => article.url.startsWith(prefix));
  }
  return articles.filter((article) => !article.url.startsWith("/projects/"));
});

const currentIndex = computed(() => {
  const currentPath = `/${page.value.relativePath.replace(/\.md$/, "")}`;
  return scopedArticles.value.findIndex(
    (article) => article.url.replace(/\.html$/, "") === currentPath,
  );
});

// Previous = older article (next in array since sorted newest first)
const prevArticle = computed(() => {
  if (currentIndex.value < 0) return null;
  return scopedArticles.value[currentIndex.value + 1] || null;
});

// Next = newer article (previous in array since sorted newest first)
const nextArticle = computed(() => {
  if (currentIndex.value < 0) return null;
  return scopedArticles.value[currentIndex.value - 1] || null;
});

const hasPrevNext = computed(() => prevArticle.value || nextArticle.value);
</script>

<template>
  <nav v-if="hasPrevNext" class="prev-next" aria-label="Article navigation">
    <a v-if="prevArticle" :href="prevArticle.url" class="prev-next-link prev">
      <span class="prev-next-label">Previous</span>
      <span class="prev-next-title">{{ prevArticle.title }}</span>
    </a>
    <span v-else class="prev-next-spacer" />

    <a v-if="nextArticle" :href="nextArticle.url" class="prev-next-link next">
      <span class="prev-next-label">Next</span>
      <span class="prev-next-title">{{ nextArticle.title }}</span>
    </a>
    <span v-else class="prev-next-spacer" />
  </nav>
</template>

<style scoped lang="scss">
@use "../../styles/abstracts/breakpoints" as *;

.prev-next {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-lg);
  margin-top: var(--space-3xl);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--color-border);

  @include md {
    grid-template-columns: 1fr 1fr;
  }
}

.prev-next-link {
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
  padding: var(--space-md);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  text-decoration: none;
  transition:
    border-color 0.2s,
    background-color 0.2s;

  &:hover {
    border-color: var(--color-accent);
    background-color: var(--color-background-soft);
  }

  &.prev {
    text-align: left;
  }

  &.next {
    text-align: left;

    @include md {
      text-align: right;
    }
  }
}

.prev-next-label {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.prev-next-title {
  font-weight: 500;
  color: var(--color-accent);
  line-height: 1.4;
}

.prev-next-spacer {
  display: block;
}
</style>
